import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  PowerSettingsNew,
  Update,
  Terminal,
  History,
  Refresh,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { api } from '../../services/api';
import type { Device } from '../../types';

interface DeviceControlDialogProps {
  open: boolean;
  device: Device | null;
  onClose: () => void;
}

export const DeviceControlDialog: React.FC<DeviceControlDialogProps> = ({
  open,
  device,
  onClose,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [customCommand, setCustomCommand] = useState('');
  const [updateVersion, setUpdateVersion] = useState('');

  // Загрузка истории команд
  const { data: commandHistory } = useQuery({
    queryKey: ['device-commands', device?.id],
    queryFn: () => device ? api.getDeviceCommandHistory(device.id) : Promise.resolve(null),
    enabled: open && !!device,
  });

  // Мутации для команд
  const restartMutation = useMutation({
    mutationFn: (params: { deviceId: number; force: boolean }) =>
      api.restartDevice(params.deviceId, params.force),
    onSuccess: () => {
      enqueueSnackbar(t('devices.restartCommandSent'), { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar(error.message || t('common.error'), { variant: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (params: { deviceId: number; version?: string }) =>
      api.updateDeviceSoftware(params.deviceId, params.version),
    onSuccess: () => {
      enqueueSnackbar(t('devices.updateCommandSent'), { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar(error.message || t('common.error'), { variant: 'error' });
    },
  });

  const commandMutation = useMutation({
    mutationFn: (params: { deviceId: number; command_type: string; command_data?: any }) =>
      api.sendDeviceCommand(params.deviceId, params),
    onSuccess: () => {
      enqueueSnackbar(t('devices.commandSent'), { variant: 'success' });
      setCustomCommand('');
    },
    onError: (error: any) => {
      enqueueSnackbar(error.message || t('common.error'), { variant: 'error' });
    },
  });

  const handleRestart = (force = false) => {
    if (device) {
      restartMutation.mutate({ deviceId: device.id, force });
    }
  };

  const handleUpdate = () => {
    if (device) {
      updateMutation.mutate({ 
        deviceId: device.id, 
        version: updateVersion || undefined 
      });
    }
  };

  const handleCustomCommand = () => {
    if (device && customCommand.trim()) {
      commandMutation.mutate({
        deviceId: device.id,
        command_type: 'custom',
        command_data: { command: customCommand.trim() }
      });
    }
  };

  if (!device) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            🎛️ {t('devices.liveControl')} - {device.name}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Быстрые команды */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {t('devices.quickCommands')}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PowerSettingsNew color="warning" sx={{ mr: 1 }} />
                      <Typography variant="subtitle1">
                        {t('devices.restart')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleRestart(false)}
                        disabled={restartMutation.isPending}
                      >
                        {t('devices.softRestart')}
                      </Button>
                      <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        onClick={() => handleRestart(true)}
                        disabled={restartMutation.isPending}
                      >
                        {t('devices.forceRestart')}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Update color="primary" sx={{ mr: 1 }} />
                      <Typography variant="subtitle1">
                        {t('devices.updateSoftware')}
                      </Typography>
                    </Box>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder={t('devices.versionOptional')}
                      value={updateVersion}
                      onChange={(e) => setUpdateVersion(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleUpdate}
                      disabled={updateMutation.isPending}
                      startIcon={updateMutation.isPending ? <CircularProgress size={16} /> : null}
                    >
                      {t('devices.update')}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Произвольная команда */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              <Terminal sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('devices.customCommand')}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('devices.enterCommand')}
                value={customCommand}
                onChange={(e) => setCustomCommand(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleCustomCommand()}
              />
              <Button
                variant="contained"
                onClick={handleCustomCommand}
                disabled={!customCommand.trim() || commandMutation.isPending}
                startIcon={commandMutation.isPending ? <CircularProgress size={16} /> : null}
              >
                {t('devices.send')}
              </Button>
            </Box>
          </Grid>

          {/* История команд */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              <History sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('devices.commandHistory')}
            </Typography>

            {commandHistory?.commands?.length ? (
              <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                {commandHistory.commands.slice(0, 5).map((cmd) => (
                  <Alert 
                    key={cmd.id} 
                    severity={cmd.status === 'completed' ? 'success' : cmd.status === 'failed' ? 'error' : 'info'}
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="body2">
                      <strong>{cmd.command_type}</strong> - {cmd.status}
                      {cmd.created_at && (
                        <Typography variant="caption" sx={{ ml: 1, opacity: 0.7 }}>
                          {new Date(cmd.created_at).toLocaleString()}
                        </Typography>
                      )}
                    </Typography>
                  </Alert>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('devices.noCommands')}
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceControlDialog;